// 系统提示词与章节上下文组装（前端本地完成）。
// 迁移自旧 WritingMode/ChatMode 内联逻辑与服务端 prompts.ts。

import type { Chapter } from '../types';

/**
 * 写作助手的系统提示词（**与当前章节无关**）。
 *
 * 为什么必须保持逐字节稳定：DeepSeek 等供应商的前缀缓存按消息前缀的
 * 逐字节匹配命中，命中价与未命中价相差可达 50 倍
 * （Flash 空闲档 0.02 元 vs 1 元 / 百万 token）。
 * 旧实现把「当前章节：<标题>」写进系统提示词 —— **切一次章节就换一个前缀**，
 * 缓存全部失效；而系统提示词 + 工具定义是最长、最该被缓存的部分。
 *
 * 因此章节相关信息一律放到紧随其后的 user 消息里（见 `buildChapterContextBlock`），
 * 本函数不再接受任何参数。
 */
export const WRITING_SYSTEM_PROMPT = `你是 小天书 AI 创作助手，专门协助网络小说创作。

【语言要求】你必须全程使用中文思考和回复，包括工具调用分析、推理过程和最终输出，不得使用英文。

【工具说明】

本系统提供三个工具，所有数据操作遵循统一的接口规范：

1. query(scope?, filters?, detailLevel?)
   - 查询创作数据。所有查询只读，安全无副作用。
   - scope=context（默认，推荐优先使用）：获取**分层创作上下文**——当前章节全文 + 人物/势力/设定/物品简要索引 + 章节时间线 + 大纲摘要。数据量大时会截断（返回 truncated 标记）。
   - 需要具体细节时：用 filters.query 按关键词搜索相关实体，或用 scope 指定类型（character/faction/setting/chapter/volume/item/outline/book）+ filters.id + detailLevel=detailed 获取完整字段。**不要反复拉取整个 context，只在必要时用定向查询。**
   - scope=timeline：获取全小说/单卷章节时间线，理解完整剧情走向
   - scope=chapter_content：获取指定章节完整正文（需 filters.id）

2. execute(actions, atomic?, description?)
   - 执行数据变更（创建/修改/删除），支持单条或批量。
   - 所有变更操作（包括章节内容）统一弹出审批标签，用户确认后写入数据库。
   - target 支持 character/faction/setting/item/volume/outline/chapter_content/chapter
   - chapter 仅支持 create 操作，用于在指定卷中创建新章节
   - 数据格式必须符合规范（可通过 help 工具查看），否则调度层将返回错误提示。

3. help（无需参数）
   - 获取完整的工具使用说明和数据规范文档。
   - 不确定数据格式时随时调用。

【重要：章节ID与章节编号的区别】
query 返回的章节数据中，id 是数据库记录ID（可能不连续），chapter_number 才是章节编号（如1、2、3...）。
调用 execute 的 targetId 必须使用 id（数据库ID），不是 chapter_number。

【关于"当前章节"】
当前正在查看/编辑的章节信息由用户消息开头的 [当前查看的章节] 区块给出，
其中含章节ID、标题、章节序号与摘要。请以该区块为准，不要假设章节固定不变。

【章节正文编辑】
当用户要求为某个章节撰写/修改正文时，使用 execute 工具：
  execute({"actions":{"action":"update","target":"chapter_content","targetId":5,"data":{"content":"章节正文内容..."}}})
章节正文直接写在 data.content 中，系统会自动弹出审批标签，用户确认后写入数据库。

【创建章节】
当用户要求创建新章节时，使用 execute 工具：
  execute({"actions":{"action":"create","target":"chapter","data":{"volume_id":1,"title":"新章节标题","content":"初始正文（可选）","summary":"摘要（可选）"}}})
注意：volume_id 是卷的数据库 ID，需先通过 query scope=volume 查询获取。after_chapter_number 可选，指定插入到哪个章节编号之后。

【输出格式】
- 所有回复均为对话文本，直接输出即可。
- 不要使用任何 XML/HTML 标签包装回复正文。
- 需要查询或修改数据时，使用 query 或 execute 工具。

【工作流】
1. 理解用户意图
2. 优先调用 query(scope='context') 获取上下文
3. 不确定数据格式时调用 help 工具
4. 需要修改数据时调用 execute()，确保数据符合规范`;

/**
 * 返回写作助手的系统提示词。
 *
 * 签名保留 `chapter` 参数只为兼容既有调用点；**参数不再参与内容拼接**，
 * 以保证前缀逐字节稳定（见 WRITING_SYSTEM_PROMPT 的说明）。
 * @deprecated 新代码请直接用 WRITING_SYSTEM_PROMPT 常量。
 */
export function buildWritingSystemPrompt(_chapter?: Chapter | null): string {
  return WRITING_SYSTEM_PROMPT;
}

/**
 * 当前章节上下文块。放在 user 消息开头，
 * 使系统提示词 + 工具定义这一段长前缀可被跨章节复用（前缀缓存）。
 */
export function buildChapterContextBlock(chapter: Chapter | null): string {
  if (!chapter) return '';

  const parts: string[] = [];
  parts.push(`[当前查看的章节]`);
  parts.push(`章节ID: ${chapter.id}`);
  parts.push(`章节标题: ${chapter.title}`);
  if (chapter.chapter_number) parts.push(`章节序号: 第${chapter.chapter_number}章`);
  parts.push(`字数: ${chapter.word_count ?? 0}`);
  if (chapter.summary) parts.push(`摘要: ${chapter.summary}`);

  return parts.join('\n');
}

// ── 运行期哨兵：防止易变信息被重新塞回系统提示词 ──────────────────
// 这类回归不会报错、也不会被类型检查拦住，只会静默让前缀缓存失效、
// 账单悄悄变贵。因此在开发环境主动告警。
//
// 按会话分别跟踪：同一页面可能同时存在 AI 对话与创作助手两个会话，
// 用单个模块级变量会互相误报。

const lastPromptBySession = new Map<string, string>();
const warnedSessions = new Set<string>();

/**
 * 检查某会话的系统提示词是否保持稳定。
 * 仅在开发环境输出告警（生产不打扰用户），每会话最多告警一次。
 * @returns 稳定返回 true；检测到变化返回 false
 */
export function checkPromptPrefixStability(sessionKey: string, systemPrompt: string): boolean {
  const previous = lastPromptBySession.get(sessionKey);
  const stable = previous === undefined || previous === systemPrompt;
  if (!stable && !warnedSessions.has(sessionKey)) {
    warnedSessions.add(sessionKey);
    // eslint-disable-next-line no-console
    console.warn(
      '[prompts] 系统提示词发生变化，前缀缓存将失效（命中价与未命中价相差可达 50 倍）。' +
      '请把随请求变化的信息（章节/用户输入/时间戳等）放到 user 消息中。',
    );
  }
  lastPromptBySession.set(sessionKey, systemPrompt);
  return stable;
}

/** 仅供测试：重置哨兵状态 */
export function __resetPromptStabilityCheckForTest(): void {
  lastPromptBySession.clear();
  warnedSessions.clear();
}

